import React, { useCallback, useMemo } from 'react';

import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import stopPropagation from '@/consts/stopPropogation';
import { UserType } from '@/types/schema';

import { useActionItemContext } from '../../context';
import useHovered from '../../hooks/useHovered';
import { AssignMenuProps } from '../../types';
import { disabledButtonStyles, linkButtonStyles } from '../ActionItem/styles';
import UserMenu from '../UserMenu';
import { overlay } from '../UserMenu/styles';
import AvatarButton from './AvatarButton';

const AssignMenu = ({
  groupIds,
  profileSettings,
  className,
  fixedWidth = false,
}: AssignMenuProps) => {
  const {
    actionItem,
    actions,
    isReadOnly,
    disableInteractiveTooltip = false,
  } = useActionItemContext();
  const { hovered, onMouseMove } = useHovered();

  const selectedUser = actionItem.assignedTo as UserType;
  const selectedUsers = useMemo(() => {
    if (actionItem.assignedToIds?.length) {
      return actionItem.assignedToUsers as UserType[];
    }
    return selectedUser ? [selectedUser] : [];
  }, [actionItem.assignedToIds, actionItem.assignedToUsers, selectedUser]);

  const selectedUserIds = selectedUsers.map((user) => user.id);

  const onChange = useCallback(
    (userIds: string[]) => actions?.onAssignMultipleClick(userIds, actionItem.id),
    [actionItem.id, actions],
  );

  const renderButtonComponent = useCallback(
    () => (
      <Stack
        alignItems="center"
        className={cn(
          overlay,
          'flex-1 justify-start border border-transparent rounded-8 hover:border-border hover:bg-transparent active:border-primary p-8 h-full w-full cursor-pointer',
          linkButtonStyles,
          { [disabledButtonStyles]: isReadOnly },
          { 'pointer-events-none': isReadOnly },
        )}
        direction="row">
        <AvatarButton
          hideUnassignedLabel
          classes={profileSettings?.classes}
          disableInteractiveTooltip={disableInteractiveTooltip}
          selectedUsers={selectedUsers}
          showAssignedToTooltip={profileSettings?.showAssignedToTooltip}
          showName={profileSettings?.showName || (selectedUsers?.length === 1 ? true : false)}
          size="xx-small"
        />
      </Stack>
    ),
    [
      disableInteractiveTooltip,
      isReadOnly,
      profileSettings?.classes,
      profileSettings?.showAssignedToTooltip,
      profileSettings?.showName,
      selectedUsers,
    ],
  );

  const containerClassName = cn(className, { 'w-[15rem]': fixedWidth });

  if (isReadOnly || !hovered) {
    return (
      <Stack
        alignItems="center"
        className={cn(containerClassName, 'w-full h-full')}
        onMouseMove={onMouseMove}>
        {renderButtonComponent()}
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="center"
      className={cn('w-full h-full', containerClassName)}
      onClick={stopPropagation}>
      <UserMenu
        groupIds={groupIds}
        position="bottom-start"
        selectedUserIds={selectedUserIds}
        onChange={onChange}>
        {renderButtonComponent()}
      </UserMenu>
    </Stack>
  );
};

export default React.memo(AssignMenu);
